import Flex from "components/grid/Flex";
import { useTranslation } from "react-i18next";
import HistoryContainerAndTitle from "./HistoryContainerAndTitle";
import { isAnyPresent, makeHistory } from "../../HistoryUtils";
import ConditionalLabel from "./ConditionalLabel";
import DateAndValueArrayContainer from "./DateAndValueArrayContainer";
import ConditionalLabelAndValue from "./ConditionalLabelAndValue";

const ComorbiditiesHistory = ({ anamnesisHistory }) => {
    const { t } = useTranslation();
    const history = makeHistory(anamnesisHistory);
    const isVisible = isAnyPresent(anamnesisHistory, [
        "haveDementia",
        "haveCOPD",
        "haveAsthma",
        "haveGastrointestinalBleeding",
        "haveOtherBleeding",
        "havePulmonaryHypertension",
        "haveRestlessLegsSyndrome",
        "haveNeurologicalDiseaseData",
        "haveAutoimmuneDiseaseData",
        "haveRenealInsufficiencyType",
        "haveRenealInsufficiencyDialysisType",
        "haveOrganTransplantationData",
        "haveMigraineTimesPerMonth",
        "haveCOVIDTimesInfected",
        "haveCOVIDVaccinationTimes",
        "haveThrombosesData",
        "haveDepressionType",
    ]);
    return (
        <HistoryContainerAndTitle title={t(`anamnesis.comorbidity.title`)} isVisible={isVisible}>
            <Flex item grow={0} container column style={{ margin: "5px 0px" }}>
                <ConditionalLabel
                    {...{
                        value: history.getLatestValueOf("haveDementia"),
                        label: `Z.n. ${t('anamnesis.comorbidity.dementia2')}`,
                    }}
                />
                <ConditionalLabel
                    {...{
                        value: history.getLatestValueOf("haveCOPD"),
                        label: `Z.n. ${t('anamnesis.comorbidity.haveCOPD')}`,
                    }}
                />
                <ConditionalLabel
                    {...{
                        value: history.getLatestValueOf("haveAsthma"),
                        label: `Z.n. ${t(`anamnesis.comorbidity.asthma`)}`,
                    }}
                />
                <ConditionalLabel
                    {...{
                        value: history.getLatestValueOf("haveGastrointestinalBleeding"),
                        label: `Z.n. ${t('anamnesis.comorbidity.haveGastrointestinalBleeding')}`,
                    }}
                />
                <ConditionalLabel
                    {...{
                        value: history.getLatestValueOf("haveOtherBleeding"),
                        label: `Z.n. ${t(`anamnesis.comorbidity.haveOtherBleeding`)}`,
                    }}
                />
                <ConditionalLabel
                    {...{
                        value: history.getLatestValueOf("havePulmonaryHypertension"),
                        label: `Z.n. ${t('anamnesis.comorbidity.havePulmonaryHypertension')}`,
                    }}
                />
                <ConditionalLabel
                    {...{
                        value: history.getLatestValueOf("haveRestlessLegsSyndrome"),
                        label: `Z.n. ${t('anamnesis.comorbidity.haveRestlessLegsSyndrome')}`,
                    }}
                />
                <DateAndValueArrayContainer
                    yearOnly
                    {...{
                        switchValue: history.getLatestValueOf("haveNeurologicalDisease"),
                        array: history.getLatestSubDataRows("haveNeurologicalDiseaseData"),
                        label: `Z.n. ${t('anamnesis.comorbidity.neurological-disease')}`,
                        getValue: (rows, index) => {
                            return rows
                                .filter((cell) => cell.question !== "date" && cell.answer)
                                .map((cell) => t(`anamnesis.comorbidity.${cell.answer}`))
                                .join(" - ");
                        },
                    }}
                />
                <DateAndValueArrayContainer
                    yearOnly
                    {...{
                        switchValue: history.getLatestValueOf("haveAutoimmuneDisease"),
                        array: history.getLatestSubDataRows("haveAutoimmuneDiseaseData"),
                        label: `Z.n. ${t('anamnesis.comorbidity.autoimmuneDisease')}`,
                        getValue: (rows, index) => {
                            return rows
                                .filter((cell) => cell.question !== "date" && cell.answer)
                                .map((cell) =>
                                    t(`anamnesis.comorbidity.${cell.answer}`).includes("anamnesis.comorbidity")
                                        ? cell.answer
                                        : t(`anamnesis.comorbidity.${cell.answer}`),
                                )
                                .join(" - ");
                        },
                    }}
                />
                <ConditionalLabelAndValue
                    {...{
                        switchValue: history.getLatestValueOf("haveRenealInsufficiency"),
                        value: history.getLatestValueOf("haveRenealInsufficiencyType")
                            ? t(`anamnesis.comorbidity.${history.getLatestValueOf("haveRenealInsufficiencyType")}`)
                            : "",
                        label: `Z.n. ${t('anamnesis.comorbidity.renealInsufficiency')}`,
                    }}
                />
                <ConditionalLabelAndValue
                    indent
                    {...{
                        switchValue: history.getLatestValueOf("haveRenealInsufficiencyDialysis"),
                        value: history.getLatestValueOf("haveRenealInsufficiencyDialysisType")
                            ? t(
                                  `anamnesis.comorbidity.${history.getLatestValueOf("haveRenealInsufficiencyDialysisType")}`,
                              )
                            : "",
                        label: `Z.n. ${t('anamnesis.comorbidity.dialysis')}`,
                    }}
                />
                <DateAndValueArrayContainer
                    yearOnly
                    {...{
                        switchValue: history.getLatestValueOf("haveOrganTransplantation"),
                        array: history.getLatestSubDataRows("haveOrganTransplantationData"),
                        label: `Z.n. ${t('anamnesis.comorbidity.organ-transplantation')}`,
                        translationKey: "anamnesis.comorbidity",
                    }}
                />
                <ConditionalLabelAndValue
                    {...{
                        switchValue: history.getLatestValueOf("haveMigraine"),
                        value: history.getLatestValueOf("haveMigraineTimesPerMonth")
                            ? t(`anamnesis.comorbidity.${history.getLatestValueOf("haveMigraineTimesPerMonth")}`)
                            : "",
                        label: `Z.n. ${t('anamnesis.comorbidity.migraines-per-month')}`,
                    }}
                />
                <ConditionalLabelAndValue
                    {...{
                        switchValue: history.getLatestValueOf("haveCOVID"),
                        value: history.getLatestValueOf("haveCOVIDTimesInfected")
                            ? t(`anamnesis.comorbidity.${history.getLatestValueOf("haveCOVIDTimesInfected")}`)
                            : "",
                        label: `Z.n. ${t('anamnesis.comorbidity.covid-infections')}`,
                    }}
                />
                <ConditionalLabelAndValue
                    {...{
                        switchValue: history.getLatestValueOf("haveCOVIDVaccination"),
                        value: history.getLatestValueOf("haveCOVIDVaccinationTimes")
                            ? t(`anamnesis.comorbidity.${history.getLatestValueOf("haveCOVIDVaccinationTimes")}`)
                            : "",
                        label: t('anamnesis.comorbidity.covid-vaccinations'),
                    }}
                />
                <DateAndValueArrayContainer
                    yearOnly
                    {...{
                        switchValue: history.getLatestValueOf("haveThromboses"),
                        array: history.getLatestSubDataRows("haveThrombosesData"),
                        label: `Z.n. ${t("anamnesis.comorbidity.THROMBOSES_DATA")}`,
                        translationKey: "anamnesis.comorbidity",
                    }}
                />
                <ConditionalLabelAndValue
                    {...{
                        switchValue: history.getLatestValueOf("haveDepression"),
                        value: history.getLatestValueOf("haveDepressionType")
                            ? t(`anamnesis.comorbidity.${history.getLatestValueOf("haveDepressionType")}`)
                            : "",
                        label: `Z.n. ${t("anamnesis.comorbidity.haveDepression")}`,
                    }}
                />
            </Flex>
        </HistoryContainerAndTitle>
    );
};

export default ComorbiditiesHistory;
